import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  Award,
  BadgePercent,
  Bell,
  CalendarHeart,
  Car,
  Check,
  Clock,
  Gift,
  Heart,
  Home,
  Hotel,
  Lightbulb,
  Mountain,
  Music,
  Sparkles,
  Tent,
  UtensilsCrossed,
  Users,
  X,
} from 'lucide-react';
import {
  ButtonLink,
  CardGrid,
  CheckList,
  CTABanner,
  FeatureCard,
  FeatureRow,
  Lead,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  Container,
  Steps,
} from '../components/page';
import { Avatar, Glow } from '../components/ui';
import { fadeUp, Stagger } from '../components/motion';
import SmartLink from '../components/SmartLink';

/* ---------- Shared bits ---------- */

const ComingSoonBadge = ({ className }) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1.5 rounded-full border border-amber-400/25 bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-300',
      className
    )}
  >
    <Clock className="h-3.5 w-3.5" />
    Coming Soon
  </span>
);

// "What You Get / How It Works / Why It's Great" breakdown under each benefit.
const DetailList = ({ items }) => (
  <dl className="grid gap-3">
    {items.map(([term, desc]) => (
      <div key={term} className="surface-inset p-4">
        <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-300">{term}</dt>
        <dd className="mt-1.5 text-base leading-relaxed text-slate-300">{desc}</dd>
      </div>
    ))}
  </dl>
);

const MockFrame = ({ title, icon: Icon, badge, children, glow = 'bg-brand-500/15' }) => (
  <div className="relative">
    <Glow className={clsx('-inset-8', glow)} />
    <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      <div className="mb-6 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-white">
          <Icon className="h-4 w-4 text-brand-300" />
          {title}
        </span>
        {badge}
      </div>
      {children}
    </div>
  </div>
);

/* ---------- Bespoke visuals ---------- */

const DiscountVisual = () => (
  <MockFrame title="Partner Offers" icon={BadgePercent} badge={<ComingSoonBadge />} glow="bg-amber-500/10">
    <div className="space-y-3">
      {[
        { icon: UtensilsCrossed, label: 'Restaurant', offer: '$10 off your meal' },
        { icon: Hotel, label: 'Hotel', offer: '20% off hotel stays' },
      ].map(({ icon: Icon, label, offer }) => (
        <div key={label} className="surface-inset relative flex items-center justify-between overflow-hidden p-4">
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300">
              <Icon className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
              <span className="block font-semibold text-white">{offer}</span>
            </span>
          </span>
          <span className="rounded-lg border border-dashed border-white/15 px-3 py-1.5 text-xs font-medium text-slate-500">
            Apply at checkout
          </span>
        </div>
      ))}
      <div className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-sm text-slate-400">
        <Gift className="h-4 w-4 text-amber-300" />
        Deals on restaurants, hotels, and trips
      </div>
    </div>
  </MockFrame>
);

const IdeaVisual = () => (
  <MockFrame
    title="Idea Suggestions"
    icon={Sparkles}
    badge={<span className="rounded-full bg-violet-400/10 px-2.5 py-0.5 text-xs font-semibold text-violet-300">AI</span>}
    glow="bg-violet-500/15"
  >
    <div className="space-y-3">
      {[
        { icon: Car, title: 'Plan a weekend getaway', budget: '$1,200', match: 96 },
        { icon: Lightbulb, title: 'Host a game night', budget: '$150', match: 91 },
      ].map(({ icon: Icon, title, budget, match }, i) => (
        <motion.div
          key={title}
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
          className="surface-inset flex items-center gap-4 p-4"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-400/10 text-violet-300">
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white">{title}</p>
            <p className="text-sm text-slate-500">
              Suggested budget <span className="font-medium tabular-nums text-slate-300">{budget}</span>
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-emerald-400/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">{match}%</span>
        </motion.div>
      ))}
    </div>
  </MockFrame>
);

const CurationVisual = () => (
  <MockFrame title="Curated for your group" icon={CalendarHeart} glow="bg-pink-500/10">
    <div className="space-y-3">
      {[
        { icon: Home, group: 'Roommates', activity: 'Host a potluck', color: 'text-emerald-300 bg-emerald-400/10' },
        { icon: Mountain, group: 'Friend group', activity: 'Plan a hiking day', color: 'text-amber-300 bg-amber-400/10' },
        { icon: Tent, group: 'Friend group', activity: 'Group bonfire', color: 'text-pink-300 bg-pink-400/10' },
      ].map(({ icon: Icon, group, activity, color }) => (
        <div key={activity} className="surface-inset flex items-center gap-4 p-4">
          <span className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', color)}>
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <p className="font-semibold text-white">{activity}</p>
            <p className="text-sm text-slate-500">{group}</p>
          </div>
        </div>
      ))}
    </div>
  </MockFrame>
);

const EngagementVisual = () => (
  <MockFrame title="Engagement Support" icon={Users} glow="bg-emerald-500/10">
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-2xl border border-brand-400/20 bg-brand-400/[0.07] p-4">
        <Bell className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" />
        <div>
          <p className="text-sm font-semibold text-white">Contribution reminder</p>
          <p className="text-sm text-slate-400">Your share for the weekend trip is due Friday.</p>
        </div>
      </div>
      <div className="surface-inset p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-300">Progress update</span>
          <span className="font-semibold tabular-nums text-brand-300">80%</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/[0.06]">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-brand-300"
            initial={{ width: 0 }}
            whileInView={{ width: '80%' }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3 }}
          />
        </div>
      </div>
      <div className="surface-inset flex items-center gap-3 p-4">
        <Avatar initials="KS" color="green" size="sm" />
        <span className="flex-1 text-sm text-slate-300">Kate S.</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 px-2.5 py-1 text-xs font-semibold text-amber-300">
          <Award className="h-3.5 w-3.5" />
          Top Contributor
        </span>
      </div>
    </div>
  </MockFrame>
);

/* ---------- Content ---------- */

const BENEFITS = [
  {
    id: 'group-discounts',
    icon: BadgePercent,
    accent: 'amber',
    title: 'Group Discounts: Save on What You Love',
    comingSoon: true,
    intro: 'Why spend more when you can save? Group Discounts let your group enjoy exclusive savings with our trusted partners.',
    details: [
      ['What You Get', 'Deals on restaurants, hotels, and trips—perfect for group outings or vacations.'],
      ['How It Works', 'When spending from your group pool, look for partner offers (e.g., "$10 off your meal" or "20% off hotel stays") and apply them at checkout.'],
      ['Status', "We're currently working on this feature, partnering with businesses to bring you the best deals. Stay tuned for launch updates!"],
    ],
    outro: 'Save money while creating memories—Group Discounts make every dollar go further. Available soon in Basic and Max tiers.',
    visual: <DiscountVisual />,
  },
  {
    id: 'idea-suggestions',
    icon: Lightbulb,
    accent: 'violet',
    title: 'Idea Suggestions: Inspiration for Your Group',
    intro: "Not sure what to save for? Let us help! Idea Suggestions use AI to recommend goals tailored to your group's vibe.",
    details: [
      ['What You Get', 'Smart ideas like "Plan a weekend getaway" or "Host a game night," complete with suggested budgets.'],
      ['How It Works', "Our AI analyzes your group's purpose and suggests exciting goals to get everyone on the same page."],
      ["Why It's Great", 'Perfect for groups who need a nudge to decide—say goodbye to indecision!'],
    ],
    outro: 'Turn your group\'s "What should we do?" into "Let\'s do this!" Available in the Max tier.',
    visual: <IdeaVisual />,
  },
  {
    id: 'activity-curation',
    icon: CalendarHeart,
    accent: 'pink',
    title: 'Activity Curation: Personalized Group Fun',
    intro: 'Make every goal a memorable experience with Activity Curation.',
    details: [
      ['What You Get', 'Personalized suggestions for group activities, like "Host a potluck" for roommates or "Plan a hiking day" for a friend group.'],
      ['How It Works', "We curate activities based on your group's purpose, helping you turn savings into shared moments."],
      ["Why It's Great", 'Keeps your group connected beyond the money, fostering stronger bonds through tailored experiences.'],
    ],
    outro: "From casual hangouts to big adventures, Activity Curation ensures your group's journey is as fun as the destination. Available in Basic and Max tiers.",
    visual: <CurationVisual />,
  },
  {
    id: 'engagement-support',
    icon: Users,
    accent: 'emerald',
    title: 'Engagement Support: Keep Everyone Involved',
    intro: 'Staying motivated as a group can be tough, but Engagement Support makes it easy.',
    details: [
      ['What You Get', 'Tools like contribution reminders, badges (e.g., "Top Contributor"), and progress updates to keep everyone on track.'],
      ['How It Works', 'Automated reminders nudge members to contribute, while badges add a fun, rewarding vibe to participation.'],
      ["Why It's Great", 'Ensures everyone stays engaged, reducing the risk of delays or disputes.'],
    ],
    outro: 'Engagement Support keeps your group motivated and moving forward together. Available in Basic and Max tiers.',
    visual: <EngagementVisual />,
  },
];

const EXAMPLES = [
  {
    icon: Home,
    accent: 'emerald',
    title: 'Roommates Planning a Dinner Night',
    items: [
      'Use Activity Curation to plan a potluck',
      'Engagement Support to remind everyone to chip in',
      'Soon, Group Discounts to save on dessert at a partner restaurant',
    ],
  },
  {
    icon: Music,
    accent: 'violet',
    title: 'Friends Saving for a Festival',
    items: [
      'Idea Suggestions propose a music festival goal',
      'Activity Curation adds a pre-festival karaoke night',
      'Engagement Support keeps contributions on track',
    ],
  },
  {
    icon: Heart,
    accent: 'pink',
    title: 'Family Vacation Fund',
    items: [
      'Idea Suggestions recommend a family road trip',
      'Engagement Support ensures timely savings',
      'Group Discounts (once live) cut costs on hotel stays',
    ],
  },
];

const REASONS = [
  { icon: BadgePercent, accent: 'amber', title: 'Save Money', text: 'Group Discounts (coming soon) stretch your budget further on dining, travel, and more.' },
  { icon: Sparkles, accent: 'violet', title: 'Get Inspired', text: "Idea Suggestions and Activity Curation spark creativity for your group's goals and activities." },
  { icon: Users, accent: 'emerald', title: 'Stay Connected', text: 'Engagement Support keeps everyone involved, building stronger group dynamics.' },
  { icon: Gift, accent: 'brand', title: 'Added Value', text: 'These perks make every group moment more enjoyable, from planning to celebrating.' },
];

const TIER_FEATURES = ['Group Discounts (coming soon)', 'Activity Curation', 'Engagement Support', 'Idea Suggestions'];

const TIERS = [
  { name: 'Basic Tier', price: '$5.99', cta: 'Get Basic', excluded: ['Idea Suggestions'] },
  { name: 'Max Tier', price: '$9.99', cta: 'Get Max', excluded: [], featured: true },
];

const TierCard = ({ tier }) => (
  <motion.div
    variants={fadeUp}
    className={clsx(
      'surface relative flex flex-col p-8 sm:p-10',
      tier.featured && '!border-brand-400/40 shadow-[0_0_60px_-20px_rgba(0,211,255,0.5)]'
    )}
  >
    {tier.featured && <Glow className="-right-16 -top-16 h-56 w-56 bg-brand-500/20" />}
    <div className="relative flex flex-1 flex-col">
      <h3 className="text-xl font-bold tracking-tight text-white">{tier.name}</h3>
      <p className="mt-6 flex items-baseline gap-1">
        <span className="text-5xl font-extrabold tabular-nums tracking-tight text-white">{tier.price}</span>
        <span className="text-slate-500">/month</span>
      </p>
      <ul className="mt-8 flex-1 space-y-3 border-t border-white/[0.06] pt-8">
        {TIER_FEATURES.map((feature) => {
          const included = !tier.excluded.includes(feature);
          return (
            <li key={feature} className={clsx('flex items-center gap-3', included ? 'text-slate-200' : 'text-slate-600')}>
              <span
                className={clsx(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                  included ? 'bg-brand-400/15 text-brand-300' : 'bg-white/[0.04] text-slate-600'
                )}
              >
                {included ? <Check className="h-3 w-3" strokeWidth={3} /> : <X className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span className={clsx(!included && 'line-through')}>{feature}</span>
            </li>
          );
        })}
      </ul>
      <ButtonLink to="/#get-started" variant={tier.featured ? 'primary' : 'secondary'} className="mt-10 w-full">
        {tier.cta}
      </ButtonLink>
    </div>
  </motion.div>
);

const ExtraBenefitsPage = () => (
  <>
    <PageHero
      highlight="Extra Benefits"
      title="Make Group Saving Even More Rewarding"
      description="Go beyond the basics with features designed to save you money, spark inspiration, and keep everyone engaged."
    />

    <Section className="!pt-8 sm:!pt-12">
      <Container>
        <SectionHeader title="Go Beyond the Basics with Groupmoney" />
        <Lead className="mt-8">
          At Groupmoney, we're not just about pooling money—we're about making your group's journey as exciting and rewarding as possible. That's why we've packed our platform with Extra Benefits designed to save you money, spark inspiration, and keep everyone engaged. From exclusive discounts to personalized ideas, these perks turn your shared goals into unforgettable experiences. Let's explore how these benefits take your group to the next level!
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Discover"
          title="Discover the Extra Benefits"
          description="Our Extra Benefits are here to add value at every step of your group's financial journey. Whether you're saving for a trip, a big event, or just everyday expenses, these features make it more fun, affordable, and connected."
        />
        <div className="mt-20 space-y-24 sm:mt-24 sm:space-y-32">
          {BENEFITS.map((b, i) => (
            <FeatureRow
              key={b.id}
              id={b.id}
              icon={b.icon}
              accent={b.accent}
              eyebrow={b.comingSoon ? <ComingSoonBadge /> : null}
              title={b.title}
              reverse={i % 2 === 1}
              visual={b.visual}
            >
              <p>{b.intro}</p>
              <DetailList items={b.details} />
              <p className="text-base text-slate-300">{b.outro}</p>
            </FeatureRow>
          ))}
        </div>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader eyebrow="Better Together" title="How These Benefits Work Together" />
        <Lead className="mt-8">
          Imagine this: Your group is saving for a weekend trip. Idea Suggestions recommend a beach getaway, complete with a budget. Activity Curation suggests a group bonfire as part of the plan. Engagement Support sends reminders to ensure everyone contributes on time, keeping spirits high with badges. And once Group Discounts launch, you'll save on your hotel booking—making the trip even more affordable.
        </Lead>
        <Reveal className="mx-auto mt-6 max-w-3xl text-center">
          <p className="text-lg font-semibold text-white">
            That's the Groupmoney difference: extra benefits that make saving and spending a joy.
          </p>
        </Reveal>
        <Steps
          className="mt-16"
          steps={[
            { title: 'Get inspired with Idea Suggestions' },
            { title: 'Plan activities with Activity Curation' },
            { title: 'Stay on track with Engagement Support' },
            { title: 'Save money with Group Discounts (soon)' },
          ]}
        />
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="In Practice"
          title="Real-Life Examples"
          description="Here's how these benefits shine for different groups:"
        />
        <CardGrid cols={3} className="mt-16">
          {EXAMPLES.map((ex) => (
            <FeatureCard key={ex.title} icon={ex.icon} accent={ex.accent} title={ex.title}>
              <CheckList items={ex.items} className="mt-2 text-sm" />
            </FeatureCard>
          ))}
        </CardGrid>
        <Reveal className="mx-auto mt-12 max-w-2xl text-center">
          <p className="text-lg text-slate-300">
            These benefits turn your group's goals into rewarding experiences, every step of the way.
          </p>
        </Reveal>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader eyebrow="Why It Matters" title="Why You'll Love These Extra Benefits" />
        <CardGrid cols={4} className="mt-16">
          {REASONS.map((r) => (
            <FeatureCard key={r.title} icon={r.icon} accent={r.accent} title={r.title}>
              {r.text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Reveal className="mx-auto mt-12 max-w-2xl text-center">
          <p className="text-lg text-slate-300">
            With Groupmoney, you're not just pooling money—you're unlocking a richer group experience.
          </p>
        </Reveal>
      </Container>
    </Section>

    <Section id="tiers">
      <Container>
        <SectionHeader
          eyebrow="Plans"
          title="Unlock These Benefits Today"
          description="Ready to make group saving more rewarding? These Extra Benefits are available in our paid tiers:"
        />
        <Stagger className="mx-auto mt-16 grid max-w-4xl gap-5 md:grid-cols-2" stagger={0.12}>
          {TIERS.map((tier) => (
            <TierCard key={tier.name} tier={tier} />
          ))}
        </Stagger>
      </Container>
    </Section>

    <CTABanner
      title="Sign up or upgrade to start enjoying these perks"
      note={
        <>
          Have questions? Visit the{' '}
          <SmartLink to="/resolution-center" className="font-medium text-brand-300 hover:text-white">
            Resolution Center
          </SmartLink>{' '}
          or email us at{' '}
          <a href="mailto:support@groupmoney.app" className="font-medium text-brand-300 hover:text-white">
            support@groupmoney.app
          </a>
          .
        </>
      }
    >
      <ButtonLink to="/#get-started" className="w-full sm:w-auto">
        Get Started
      </ButtonLink>
      <ButtonLink to="#tiers" variant="secondary" className="w-full sm:w-auto">
        View Pricing
      </ButtonLink>
    </CTABanner>
  </>
);

export default ExtraBenefitsPage;
